import { IProductModel } from '@masterofmalt/atom.componentlibrary/models/internal/product/IProductModel';
import deepmerge from 'deepmerge';

import { NumericMenuItem } from '@/vibes/mom/sections/faceted-product-list/types';
import { env } from '~/lib/atom/env';
import { Currency } from '~/lib/atom/types/currency';

import { BASE_PRICE_POINTS, defaultPriceFilters } from './consts';

export type RecursivePartial<T> = {
  [P in keyof T]?: T[P] extends Array<infer U>
    ? Array<RecursivePartial<U>>
    : T[P] extends object
      ? RecursivePartial<T[P]>
      : T[P];
};

export const getMockIProductModel = (
  overrides: RecursivePartial<IProductModel> = {},
): IProductModel => {
  const baseProductModel: IProductModel = {
    id: Math.floor(Math.random() * 9999999),
    urlRelative: '/rum/clarkes-court-distillery/clarkes-court-pure-white/',
    images: [
      {
        urlRelative: 'https://www.masterofmalt.com/gin/ableforths/bathtub-gin.jpg',
      },
    ],
    // Only absolute so it loads a proper images in storybook
    imageUrlRelative: 'https://www.masterofmalt.com/gin/ableforths/bathtub-gin.jpg',
    name: 'Bathtub Gin',
    shortName: 'Bathtub Gin',
    description: '',
    tags: [],
    buyStatus: 'IN_STOCK',
    releaseDate: new Date(),
    meta: {
      abv: 69,
      volume: 69,
      hasDram: false,
      isDram: false,
      sponsored: false,
      sponsoredClickUrl: null,
      showBuyButton: true,
      hasBespokePrice: false,
      distillery: {
        name: '',
        type: 'distillery',
        urlRelative: '',
      },
      bottler: {
        name: '',
        type: 'bottler',
        urlRelative: '',
      },
    },
    reviews: {
      rating: 4,
      count: 69,
    },
    price: {
      currency: 'GBP', // This will always be GBP
      current: 20,
      discountedFrom: 0,
      discountStart: null,
      discountEnd: null,
      discountType: 'NO_DISCOUNT',
      deliveryCountryShortCode: null,
      tradeGroupId: null,
    },
    prices: [
      {
        currency: 'GBP',
        current: 20.0,
        discountedFrom: null,
        discountStart: null,
        discountEnd: null,
        discountType: 'NO_DISCOUNT',
        deliveryCountryShortCode: 'GB',
        tradeGroupId: 0,
      },
    ],
    multiBuy: [],
    availability: {
      inStock: true,
      freeStock: 100,
      unlimitedStock: true, // Calculated from freeStock and stocklevel
      maxNumberPerCustomer: 69,
      expectedDate: null,
    },
    category: {
      type: { name: 'Rum', type: 'type' },
      style: { name: 'White', type: 'style' },
    },
  };

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return deepmerge(baseProductModel, overrides) as IProductModel;
};

function toMoney(amt: number): number {
  return Number(amt.toFixed(2));
}

// Function to get price filters based on current currency
export function getPriceFilters(currency: Currency): NumericMenuItem[] {
  const {
    code,
    exchangeRate = 1,
    display: { symbol = '£' },
    isTransactional,
  } = currency;

  if (code === env.NEXT_PUBLIC_DEFAULT_CURRENCY_CODE) {
    return defaultPriceFilters;
  }

  const filters: NumericMenuItem[] = [{ label: 'Show all' }];

  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < BASE_PRICE_POINTS.length; i++) {
    const basePrice = BASE_PRICE_POINTS[i];
    const nextPrice = BASE_PRICE_POINTS[i + 1];
    const current = Math.ceil(toMoney((basePrice || 0) * exchangeRate) / 10) * 10; // Round up to nearest 10
    const isLast = i === BASE_PRICE_POINTS.length - 1;

    if (isLast) {
      // Last entry is "400+"
      filters.push({
        label: `${symbol}${current}+`,
        start: !isTransactional ? basePrice : current,
      });
    } else {
      if (!nextPrice) {
        // eslint-disable-next-line no-continue
        continue;
      }

      const next = Math.ceil(toMoney(nextPrice * exchangeRate) / 10) * 10; // Round up to nearest 10

      filters.push({
        label: `${symbol}${current} - ${symbol}${next}`,
        // eslint-disable-next-line no-nested-ternary
        start: i === 0 ? undefined : !isTransactional ? basePrice : current,
        end: !isTransactional ? nextPrice : next,
      });
    }
  }

  return filters;
}
